/**
 * On-Road Price Calculator
 * -----------------------------------------------------------------------------
 * A four step selector (Brand -> Model -> Variant -> State) that reads pricing
 * from a published Google Sheet and reconciles the sheet's rows against the
 * store's own products so the result screen can add the real variant to cart.
 *
 * Source of truth split:
 *   - Google Sheet : which variants are priceable, and every rupee figure shown.
 *   - Shopify      : brands (blocks), models (products), variant IDs for cart.
 *
 * Sheet shape (headers matched by name, not position):
 *   Brand       -> the MODEL, e.g. "Jawa Perak", "Yezdi Adventure"
 *   Model Name  -> the VARIANT / colour, e.g. "Tornado Black"
 *   State       -> the RTO state
 *   ...then the money columns.
 *
 * The word "Brand" in the sheet is therefore model-level. The shopper-facing
 * brand (Jawa / Yezdi / BSA) comes from the section's blocks, and every row is
 * resolved to a real product + variant once, up front, at ingest time.
 */
(function () {
  "use strict";

  /* ---------------------------------------------------------------------- */
  /* Constants                                                              */
  /* ---------------------------------------------------------------------- */

  var STEPS = ["brand", "model", "variant", "state"];
  var STEP_LABELS = {
    brand: "Brand",
    model: "Model",
    variant: "Variant",
    state: "State",
  };
  var CACHE_PREFIX = "onrp:sheet:";

  /**
   * Header aliases. Order matters: `variant` is resolved before `model` so a
   * "Model Name" column is claimed as the variant/colour. The sheet's "Brand"
   * column is model-level ("Jawa Perak"), so it feeds `model`.
   */
  var COLUMNS = {
    variant: [
      "model name",
      "modelname",
      "variant",
      "variant name",
      "colour",
      "color",
    ],
    model: ["brand", "model", "series", "make"],
    state: ["state"],
    exShowroom: [
      "ex-showroom",
      "ex showroom",
      "ex-showroom price",
      "ex showroom price",
      "exshowroom",
    ],
    rto: ["rto tax", "rto", "road tax"],
    registration: [
      "registration & charges",
      "registration and charges",
      "registration charges",
      "registration",
      "reg charges",
      "registration fee",
    ],
    handling: ["handling charges", "handling", "logistics charges"],
    insurance: [
      "insurance",
      "insurance (1yr)",
      "insurance 1yr",
      "insurance 1 yr",
    ],
    onRoad: [
      "on-road price",
      "on road price",
      "onroad price",
      "total on-road price",
      "total on road price",
    ],
  };

  var BREAKDOWN_ROWS = [
    { key: "exShowroom", label: "Ex-Showroom Price" },
    { key: "rto", label: "RTO Tax" },
    { key: "registration", label: "Registration Charges" },
    { key: "handling", label: "Handling Charges" },
    { key: "insurance", label: "Insurance" },
  ];

  var ICONS = {
    check:
      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    arrow:
      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 8h11m0 0L9 3.5M13.5 8L9 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    arrowLeft:
      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M13.5 8h-11m0 0L7 3.5M2.5 8L7 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    search:
      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.5"/><path d="M10.5 10.5L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    pin: '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 14s5-4.2 5-8A5 5 0 0 0 3 6c0 3.8 5 8 5 8z" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="6" r="1.75" stroke="currentColor" stroke-width="1.4"/></svg>',
    chevron:
      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 10L8 5.5l4.5 4.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    info: '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.2"/><path d="M8 7.25v4M8 5.1v.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
  };

  /* ---------------------------------------------------------------------- */
  /* Helpers                                                                */
  /* ---------------------------------------------------------------------- */

  /** Loose key used for every sheet <-> Shopify comparison. */
  function norm(value) {
    return String(value == null ? "" : value)
      .toLowerCase()
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }

  /**
   * Sheets often carry a stray trailing word, e.g. "… Matte Grey Price".
   * Only used as a fallback after an exact comparison has already failed, so a
   * genuine colour ending in one of these words still wins on the first pass.
   */
  function trimTrailingNoise(value) {
    return value.replace(/\s+(price|colour|color)$/, "").trim();
  }

  /**
   * Strip marketing decoration from a product title so a sheet saying
   * "Yezdi Scrambler" still resolves to a product called
   * "New Yezdi Scrambler 2026". Only leading qualifiers, emission/compliance
   * suffixes and a trailing model year are removed, so genuinely distinct
   * names ("Jawa 42" vs "Jawa 42 FJ") stay distinct.
   */
  function stripModelDecorations(value) {
    return value
      .replace(/^(all new|the new|brand new|new|the)\s+/, "")
      .replace(/\s+(bs6|bs4|bs vi|bs iv|obd2|obd 2|obd 2b|e20)\b/g, "")
      .replace(/\s+(19|20)\d{2}$/, "")
      .trim();
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value).replace(
      /[&<>"']/g,
      function (char) {
        return {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[char];
      },
    );
  }

  /** "₹2,49,990" / "249990.00" / "" -> 249990 | null */
  function parseAmount(value) {
    if (value == null) return null;
    var cleaned = String(value).replace(/[^0-9.\-]/g, "");
    if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
    var num = Number(cleaned);
    return isNaN(num) ? null : num;
  }

  function formatMoney(amount, currency) {
    if (amount == null) return "—";
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (error) {
      return "₹" + Math.round(amount).toLocaleString("en-IN");
    }
  }

  function unique(values) {
    var seen = Object.create(null);
    var out = [];
    values.forEach(function (value) {
      var key = norm(value);
      if (!key || seen[key]) return;
      seen[key] = true;
      out.push(value);
    });
    return out;
  }

  /** RFC 4180-ish parser. Prices arrive quoted ("2,49,990") so quotes matter. */
  function parseCsv(text) {
    var rows = [];
    var row = [];
    var field = "";
    var inQuotes = false;
    var i = 0;

    while (i < text.length) {
      var char = text[i];

      if (inQuotes) {
        if (char === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i += 2;
            continue;
          }
          inQuotes = false;
          i++;
          continue;
        }
        field += char;
        i++;
        continue;
      }

      if (char === '"') {
        inQuotes = true;
        i++;
        continue;
      }
      if (char === ",") {
        row.push(field);
        field = "";
        i++;
        continue;
      }
      if (char === "\r") {
        i++;
        continue;
      }
      if (char === "\n") {
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
        i++;
        continue;
      }
      field += char;
      i++;
    }

    if (field !== "" || row.length) {
      row.push(field);
      rows.push(row);
    }

    return rows.filter(function (cells) {
      return cells.some(function (cell) {
        return cell.trim() !== "";
      });
    });
  }

  /**
   * Accepts a published CSV link, a gviz link, or a plain /edit link and always
   * returns something fetchable. Merchants paste whatever is in their address
   * bar, so this has to be forgiving.
   */
  function resolveSheetUrl(rawUrl, tabName) {
    var url = String(rawUrl || "").trim();
    if (!url) return "";
    if (/output=csv|tqx=out:csv|\.csv($|\?)/i.test(url)) return url;

    var idMatch = url.match(/\/spreadsheets\/d\/(?:e\/)?([a-zA-Z0-9-_]+)/);
    if (!idMatch) return url;

    var base =
      "https://docs.google.com/spreadsheets/d/" +
      idMatch[1] +
      "/gviz/tq?tqx=out:csv";
    var gidMatch = url.match(/[#&?]gid=([0-9]+)/);
    if (gidMatch) return base + "&gid=" + gidMatch[1];
    if (tabName) return base + "&sheet=" + encodeURIComponent(tabName);
    return base;
  }

  function readCache(key, ttlMinutes) {
    if (!ttlMinutes) return null;
    try {
      var raw = window.sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (Date.now() - parsed.t > ttlMinutes * 60000) return null;
      return parsed.v;
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, value, ttlMinutes) {
    if (!ttlMinutes) return;
    try {
      window.sessionStorage.setItem(
        CACHE_PREFIX + key,
        JSON.stringify({ t: Date.now(), v: value }),
      );
    } catch (error) {
      /* Quota or private mode — cache is a nicety, never a requirement. */
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Component                                                              */
  /* ---------------------------------------------------------------------- */

  function OnRoadPrice(root) {
    this.root = root;
    this.config = this.readConfig();
    this.rows = [];
    this.brands = [];
    this.modelLabels = {};
    this.selection = { brand: null, model: null, variant: null, state: null };
    this.query = "";
    this.breakdownOpen = true;
    this.stepIndex = 0;
    this.finished = false;

    this.els = {
      steps: root.querySelector("[data-onrp-steps]"),
      panel: root.querySelector("[data-onrp-panel]"),
      actionbar: root.querySelector("[data-onrp-actionbar]"),
      back: root.querySelector("[data-onrp-hero-back]"),
      backBtn: root.querySelector("[data-onrp-back]"),
      continue: root.querySelector("[data-onrp-continue]"),
      live: root.querySelector("[data-onrp-live]"),
    };

    this.applyBackLink();
    this.bind();
    this.load();
  }

  OnRoadPrice.prototype.readConfig = function () {
    var script = this.root.querySelector("[data-onrp-config]");
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error("[on-road-price] Invalid section config.", error);
      return { brands: [] };
    }
  };

  /**
   * Resolve where "Back to motorcycle" should point.
   * Priority:
   *   1. ?return= / ?back= parameter on the URL (set by the PDP link).
   *   2. document.referrer, when it is a same-origin product page.
   *   3. The URL entered in the theme editor.
   * A same-origin guard keeps this from ever becoming an open redirect.
   */
  OnRoadPrice.prototype.resolveBackUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var fromParam = params.get("return") || params.get("back");

    function sameOrigin(url) {
      try {
        var parsed = new URL(url, window.location.origin);
        return parsed.origin === window.location.origin
          ? parsed.pathname + parsed.search + parsed.hash
          : "";
      } catch (error) {
        return "";
      }
    }

    if (fromParam) {
      var safe = sameOrigin(fromParam);
      if (safe) return safe;
    }

    if (this.config.backFollowReferrer && document.referrer) {
      var ref = sameOrigin(document.referrer);
      // Only trust a referrer that looks like a product page and isn't this page.
      if (
        ref &&
        ref.indexOf("/products/") !== -1 &&
        ref !== window.location.pathname
      )
        return ref;
    }

    return this.config.backUrl || "";
  };

  OnRoadPrice.prototype.applyBackLink = function () {
    var link = this.els.back;
    if (!link) return;
    var target = this.resolveBackUrl();
    if (!target) {
      link.hidden = true;
      return;
    }
    link.hidden = false;
    link.setAttribute("href", target);
  };

  /* --- Data ------------------------------------------------------------- */

  OnRoadPrice.prototype.load = function () {
    var self = this;
    var url = resolveSheetUrl(this.config.sheetUrl, this.config.sheetTab);

    if (!url) {
      this.renderState(
        "Pricing source not connected",
        "Add the published Google Sheet URL in the theme editor to switch this calculator on.",
      );
      return;
    }

    this.renderSkeleton();

    var cached = readCache(url, this.config.cacheMinutes);
    if (cached) {
      this.ingest(cached);
      return;
    }

    fetch(url, { cache: "no-store" })
      .then(function (response) {
        if (!response.ok) throw new Error("HTTP " + response.status);
        return response.text();
      })
      .then(function (text) {
        if (/<\s*html/i.test(text.slice(0, 200))) {
          throw new Error(
            "Sheet returned HTML — it is probably not published to the web.",
          );
        }
        writeCache(url, text, self.config.cacheMinutes);
        self.ingest(text);
      })
      .catch(function (error) {
        console.error("[on-road-price] Sheet fetch failed.", error);
        self.renderState(
          "Prices could not be loaded",
          "The pricing sheet did not respond. Check your connection and try again.",
          true,
        );
      });
  };

  OnRoadPrice.prototype.ingest = function (csvText) {
    var table = parseCsv(csvText);
    if (table.length < 2) {
      this.renderState(
        "Pricing sheet is empty",
        "Add at least one pricing row to the sheet, then reload this page.",
      );
      return;
    }

    var headers = table[0].map(function (cell) {
      return norm(cell);
    });
    var map = {};
    var taken = {};

    Object.keys(COLUMNS).forEach(function (field) {
      for (var i = 0; i < headers.length; i++) {
        if (taken[i]) continue;
        if (COLUMNS[field].indexOf(headers[i]) !== -1) {
          map[field] = i;
          taken[i] = true;
          return;
        }
      }
    });

    var missing = ["model", "variant", "state"].filter(function (field) {
      return map[field] == null;
    });
    if (missing.length) {
      this.renderState(
        "Pricing sheet columns do not match",
        "These columns are required and were not found: " +
          missing.join(", ") +
          ". Expected a model column (Brand), a variant column (Model Name) and State.",
      );
      return;
    }

    var raw = table
      .slice(1)
      .map(function (cells) {
        function cell(field) {
          return map[field] == null
            ? ""
            : String(cells[map[field]] || "").trim();
        }
        return {
          model: cell("model"),
          variant: cell("variant"),
          state: cell("state"),
          exShowroom: parseAmount(cell("exShowroom")),
          rto: parseAmount(cell("rto")),
          registration: parseAmount(cell("registration")),
          handling: parseAmount(cell("handling")),
          insurance: parseAmount(cell("insurance")),
          onRoad: parseAmount(cell("onRoad")),
        };
      })
      .filter(function (row) {
        return row.model && row.variant && row.state;
      });

    if (!raw.length) {
      this.renderState(
        "No usable pricing rows",
        "Every row needs a model, a variant name and a state before it can be priced.",
      );
      return;
    }

    this.resolveRows(raw);

    if (!this.rows.length) {
      this.renderState(
        "No pricing rows match this store",
        "None of the sheet rows line up with the products in the brand collections. Open the browser console for the exact names that could not be matched.",
      );
      return;
    }

    var self = this;
    this.brands = this.config.brands.filter(function (brand) {
      return self.rows.some(function (row) {
        return row.brandId === brand.id;
      });
    });

    if (!this.brands.length) {
      this.renderState(
        "No brands available",
        "None of the brands configured in the theme editor have priced products. Check the collection on each brand block.",
      );
      return;
    }

    this.applyDeepLink();
    this.render();
  };

  /* --- Matching --------------------------------------------------------- */

  /**
   * Names a sheet row could plausibly use for this product. The brand-prefixed
   * forms are what let a sheet saying "Jawa Perak" or "Yezdi Adventure" resolve
   * to products titled simply "Perak" and "Adventure".
   *
   * Comparisons are exact equality against this list, never substring, so
   * "Jawa 42" can not accidentally swallow "Jawa 42 FJ".
   */
  OnRoadPrice.prototype.productKeys = function (brand, product) {
    var brandKey = brand && brand.name ? norm(brand.name) : "";
    var keys = [];

    function push(value) {
      if (value && keys.indexOf(value) === -1) keys.push(value);
    }

    [product.title, product.handle].forEach(function (source) {
      var base = norm(source);
      [base, stripModelDecorations(base)].forEach(function (form) {
        if (!form) return;
        push(form);
        if (brandKey) push(brandKey + " " + form);
      });
    });

    return keys;
  };

  /**
   * Names a sheet row could use for this variant. Single-variant products
   * report "Default Title", so the product title stands in for those.
   */
  OnRoadPrice.prototype.variantKeys = function (product, variant) {
    var keys = [];
    if (variant.title && norm(variant.title) !== "default title") {
      keys.push(variant.title, product.title + " " + variant.title);
    }
    keys.push(product.title);
    if (variant.sku) keys.push(variant.sku);
    return keys.map(norm);
  };

  /**
   * Resolve every sheet row to a concrete brand block, product and variant
   * exactly once. Everything downstream is then a cheap array filter, and any
   * row that cannot be resolved is reported rather than silently swallowed.
   */
  OnRoadPrice.prototype.resolveRows = function (raw) {
    var self = this;
    var resolved = [];
    var unmatchedModels = Object.create(null);
    var unmatchedVariants = Object.create(null);
    var seen = Object.create(null);
    var labels = Object.create(null);
    var duplicates = 0;

    raw.forEach(function (row) {
      var modelKey = norm(row.model);
      var variantKey = norm(row.variant);
      var variantAlt = trimTrailingNoise(variantKey);
      var hit = null;

      self.config.brands.some(function (brand) {
        return (brand.models || []).some(function (product) {
          if (self.productKeys(brand, product).indexOf(modelKey) === -1) {
            return false;
          }

          // Product matched — now find the variant inside it.
          var variantHit = null;
          product.variants.some(function (variant) {
            var keys = self.variantKeys(product, variant);
            if (
              keys.indexOf(variantKey) !== -1 ||
              keys.indexOf(variantAlt) !== -1
            ) {
              variantHit = variant;
              return true;
            }
            return false;
          });

          if (!variantHit) {
            unmatchedVariants[row.model + " › " + row.variant] = true;
            return false;
          }

          hit = { brand: brand, product: product, variant: variantHit };
          return true;
        });
      });

      if (!hit) {
        if (!unmatchedVariants[row.model + " › " + row.variant]) {
          unmatchedModels[row.model] = true;
        }
        return;
      }

      // One price per variant + state. Later duplicates are ignored.
      var key = hit.variant.id + "|" + norm(row.state);
      if (seen[key]) {
        duplicates++;
        return;
      }
      seen[key] = true;

      // The sheet's wording is what the shopper sees; the Shopify product is
      // still what goes in the cart. First spelling in the sheet wins.
      if (!labels[hit.product.id]) labels[hit.product.id] = row.model;

      row.brandId = hit.brand.id;
      row.productId = hit.product.id;
      row.variantId = hit.variant.id;
      resolved.push(row);
    });

    this.rows = resolved;
    this.modelLabels = labels;
    this.reportUnmatched(unmatchedModels, unmatchedVariants, duplicates);
  };

  /**
   * Console diagnostics. Populating the sheet is the slow part of going live,
   * so the names that failed to match are printed verbatim to make the fix
   * obvious rather than requiring a hunt.
   */
  OnRoadPrice.prototype.reportUnmatched = function (models, variants, dupes) {
    var modelList = Object.keys(models);
    var variantList = Object.keys(variants);
    var known = [];
    this.config.brands.forEach(function (brand) {
      (brand.models || []).forEach(function (product) {
        known.push(brand.name + " › " + product.title);
      });
    });

    if (modelList.length) {
      console.warn(
        "[on-road-price] " +
          modelList.length +
          " sheet model name(s) did not match any product in the brand collections. " +
          "Rename them in the sheet, or retitle the product to match:\n  " +
          modelList.join("\n  ") +
          "\n\nProducts available in the brand collections:\n  " +
          known.join("\n  "),
      );
    }
    if (variantList.length) {
      console.warn(
        "[on-road-price] " +
          variantList.length +
          " sheet variant name(s) matched a product but none of its variants:\n  " +
          variantList.join("\n  "),
      );
    }
    if (dupes) {
      console.warn(
        "[on-road-price] " +
          dupes +
          " duplicate row(s) for the same variant and state were ignored; the first occurrence wins.",
      );
    }
  };

  /* --- Derived lists ---------------------------------------------------- */

  /**
   * Display name for a model: the sheet's wording when we have it, falling back
   * to the Shopify product title. Cart operations always use the variant id, so
   * the label never affects what is actually purchased.
   */
  OnRoadPrice.prototype.modelLabel = function (product) {
    return this.modelLabels[product.id] || product.title;
  };

  OnRoadPrice.prototype.models = function () {
    var brand = this.selection.brand;
    if (!brand) return [];
    var rows = this.rows;
    return (brand.models || []).filter(function (product) {
      return rows.some(function (row) {
        return row.productId === product.id;
      });
    });
  };

  /**
   * Variants are listed with the sheet's own wording (that is the label the
   * shopper sees) but carry the Shopify variant they were matched to.
   */
  OnRoadPrice.prototype.variants = function () {
    var product = this.selection.model;
    if (!product) return [];

    var rows = this.rows;
    var out = [];
    product.variants.forEach(function (variant) {
      var mine = rows.filter(function (row) {
        return row.variantId === variant.id;
      });
      if (!mine.length) return;

      var prices = mine
        .map(function (row) {
          return row.exShowroom;
        })
        .filter(function (value) {
          return value != null;
        });

      out.push({
        id: variant.id,
        label: mine[0].variant,
        shopifyTitle: variant.title,
        available: variant.available,
        variant: variant,
        exShowroom: prices.length ? Math.min.apply(null, prices) : null,
      });
    });
    return out;
  };

  OnRoadPrice.prototype.modelFromPrice = function (product) {
    var prices = this.rows
      .filter(function (row) {
        return row.productId === product.id;
      })
      .map(function (row) {
        return row.exShowroom;
      })
      .filter(function (value) {
        return value != null;
      });
    return prices.length ? Math.min.apply(null, prices) : null;
  };

  OnRoadPrice.prototype.states = function () {
    var variant = this.selection.variant;
    if (!variant) return [];
    return unique(
      this.rows
        .filter(function (row) {
          return row.variantId === variant.id;
        })
        .map(function (row) {
          return row.state;
        }),
    ).sort(function (a, b) {
      return a.localeCompare(b);
    });
  };

  OnRoadPrice.prototype.priceRow = function () {
    var variant = this.selection.variant;
    var state = this.selection.state;
    if (!variant || !state) return null;
    return (
      this.rows.filter(function (row) {
        return row.variantId === variant.id && norm(row.state) === norm(state);
      })[0] || null
    );
  };

  /* --- Deep link from the PDP ------------------------------------------- */

  OnRoadPrice.prototype.applyDeepLink = function () {
    var params = new URLSearchParams(window.location.search);

    var brandParam = params.get("brand");
    if (!brandParam) return;

    var brand = this.brands.filter(function (item) {
      return norm(item.name) === norm(brandParam);
    })[0];
    if (!brand) return;
    this.selection.brand = brand;
    this.stepIndex = 1;

    var modelParam = params.get("model");
    var product = this.models().filter(function (item) {
      return (
        norm(item.handle) === norm(modelParam) ||
        norm(item.title) === norm(modelParam)
      );
    })[0];
    if (!product) return;
    this.selection.model = product;
    this.stepIndex = 2;

    var variantId = params.get("variant_id") || params.get("variant");
    var match = this.variants().filter(function (item) {
      return (
        String(item.id) === String(variantId) ||
        norm(item.label) === norm(variantId) ||
        norm(item.shopifyTitle) === norm(variantId)
      );
    })[0];
    if (!match) return;
    this.selection.variant = match;
    this.stepIndex = 3;

    var stateParam = params.get("state");
    var state = this.states().filter(function (item) {
      return norm(item) === norm(stateParam);
    })[0];
    if (!state) return;
    this.selection.state = state;
    this.finished = true;
  };

  OnRoadPrice.prototype.syncUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams(window.location.search);
    var sel = this.selection;

    sel.brand ? params.set("brand", sel.brand.name) : params.delete("brand");
    sel.model ? params.set("model", sel.model.handle) : params.delete("model");
    sel.variant
      ? params.set("variant_id", sel.variant.id)
      : params.delete("variant_id");
    params.delete("variant");
    // Legacy links from the five step version may still carry a city.
    params.delete("city");
    sel.state && this.finished
      ? params.set("state", sel.state)
      : params.delete("state");

    var query = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (query ? "?" + query : ""),
    );
  };

  /* --- Step machine ----------------------------------------------------- */

  OnRoadPrice.prototype.currentStep = function () {
    return STEPS[this.stepIndex];
  };

  OnRoadPrice.prototype.canContinue = function () {
    return Boolean(this.selection[this.currentStep()]);
  };

  OnRoadPrice.prototype.select = function (step, value) {
    // Selecting upstream always clears downstream — no stale state on a new variant.
    var index = STEPS.indexOf(step);
    STEPS.slice(index).forEach(function (key) {
      this.selection[key] = null;
    }, this);
    this.selection[step] = value;
    this.finished = false;
    this.query = "";
    this.render();
  };

  OnRoadPrice.prototype.goTo = function (index) {
    this.stepIndex = Math.max(0, Math.min(STEPS.length - 1, index));
    this.finished = false;
    this.query = "";
    this.render();
    this.focusPanel();
  };

  OnRoadPrice.prototype.next = function () {
    if (!this.canContinue()) return;
    if (this.stepIndex === STEPS.length - 1) {
      this.finished = true;
      this.render();
      this.focusPanel();
      return;
    }
    this.goTo(this.stepIndex + 1);
  };

  OnRoadPrice.prototype.focusPanel = function () {
    var panel = this.els.panel;
    if (!panel) return;
    panel.setAttribute("tabindex", "-1");
    panel.focus({ preventScroll: true });
    var top = this.root.querySelector("[data-onrp-steps]");
    if (top && top.getBoundingClientRect().top < 0) {
      top.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  /* --- Events ----------------------------------------------------------- */

  OnRoadPrice.prototype.bind = function () {
    var self = this;

    this.root.addEventListener("click", function (event) {
      var target = event.target.closest("[data-onrp-action]");
      if (!target || !self.root.contains(target)) return;
      var action = target.dataset.onrpAction;

      if (action === "step") {
        var index = Number(target.dataset.index);
        if (index <= self.stepIndex || self.finished) self.goTo(index);
        return;
      }
      if (action === "select") {
        self.pickFromDataset(target);
        return;
      }
      if (action === "continue") {
        self.next();
        return;
      }
      if (action === "back") {
        self.goTo(self.stepIndex - 1);
        return;
      }
      if (action === "retry") {
        self.load();
        return;
      }
      if (action === "toggle-breakdown") {
        self.toggleBreakdown(target);
        return;
      }
      if (action === "add-to-cart") {
        self.addToCart(target);
        return;
      }
    });

    this.root.addEventListener("input", function (event) {
      var input = event.target.closest("[data-onrp-search]");
      if (!input) return;
      self.query = input.value;
      self.renderOptions();
    });

    // Enter in the search field commits the only remaining option.
    this.root.addEventListener("keydown", function (event) {
      var input = event.target.closest("[data-onrp-search]");
      if (!input || event.key !== "Enter") return;
      event.preventDefault();
      var first = self.root.querySelector(".onrp__option");
      if (first) first.click();
    });
  };

  OnRoadPrice.prototype.pickFromDataset = function (target) {
    var step = target.dataset.step;
    var value = target.dataset.value;

    if (step === "brand") {
      this.select(
        "brand",
        this.brands.filter(function (b) {
          return b.id === value;
        })[0] || null,
      );
    } else if (step === "model") {
      this.select(
        "model",
        this.models().filter(function (m) {
          return String(m.id) === value;
        })[0] || null,
      );
    } else if (step === "variant") {
      this.select(
        "variant",
        this.variants().filter(function (v) {
          return String(v.id) === value;
        })[0] || null,
      );
    } else {
      this.select(step, value);
    }

    if (this.config.autoAdvance) this.next();
  };

  /* --- Cart ------------------------------------------------------------- */

  OnRoadPrice.prototype.addToCart = function (button) {
    var self = this;
    var variant = this.selection.variant;
    var row = this.priceRow();
    if (!variant || !row || button.disabled) return;

    var error = this.root.querySelector("[data-onrp-cart-error]");
    if (error) error.hidden = true;

    var properties = {};
    if (this.config.addProperties) {
      properties = {
        State: row.state,
        "On-Road Price": formatMoney(row.onRoad, this.config.currency),
      };
    }

    button.classList.add("is-loading");
    button.setAttribute("aria-busy", "true");

    fetch(this.config.routes.cartAdd + ".js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [{ id: variant.id, quantity: 1, properties: properties }],
      }),
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok)
            throw new Error(
              data.description || data.message || "Add to cart failed",
            );
          return data;
        });
      })
      .then(function () {
        if (self.config.cartAction === "checkout") {
          window.location.href = "/checkout";
          return;
        }
        if (self.config.cartAction === "cart") {
          window.location.href = self.config.routes.cart;
          return;
        }
        document.dispatchEvent(
          new CustomEvent("onrp:added", { detail: { variantId: variant.id } }),
        );
        button.classList.remove("is-loading");
        button.removeAttribute("aria-busy");
        self.announce("Added to cart.");
      })
      .catch(function (err) {
        console.error("[on-road-price] Add to cart failed.", err);
        button.classList.remove("is-loading");
        button.removeAttribute("aria-busy");
        if (error) {
          error.textContent = err.message + " Refresh the page and try again.";
          error.hidden = false;
        }
      });
  };

  OnRoadPrice.prototype.announce = function (message) {
    if (this.els.live) this.els.live.textContent = message;
  };

  /**
   * Accordion open/close driven by an explicit pixel height so the transition
   * is smooth in both directions, then released to `auto` so the panel can
   * still reflow (e.g. on resize). Falls back to an instant toggle when the
   * user prefers reduced motion.
   */
  OnRoadPrice.prototype.toggleBreakdown = function (toggle) {
    var body = this.root.querySelector("[data-onrp-breakdown-body]");
    if (!body) return;

    var willOpen = !this.breakdownOpen;
    this.breakdownOpen = willOpen;
    toggle.setAttribute("aria-expanded", willOpen ? "true" : "false");

    var reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      body.hidden = !willOpen;
      body.style.height = "";
      return;
    }

    // Cancel any transition mid-flight.
    body.style.transition = "none";

    if (willOpen) {
      body.hidden = false;
      body.style.height = "0px";
      // Force reflow so the browser registers the start height.
      void body.offsetHeight;
      body.style.transition = "";
      body.style.height = body.scrollHeight + "px";
      body.classList.add("is-animating");
      var onOpenEnd = function () {
        body.style.height = "auto";
        body.classList.remove("is-animating");
        body.removeEventListener("transitionend", onOpenEnd);
      };
      body.addEventListener("transitionend", onOpenEnd);
    } else {
      body.style.height = body.scrollHeight + "px";
      void body.offsetHeight;
      body.style.transition = "";
      body.classList.add("is-animating");
      body.style.height = "0px";
      var onCloseEnd = function () {
        body.hidden = true;
        body.style.height = "";
        body.classList.remove("is-animating");
        body.removeEventListener("transitionend", onCloseEnd);
      };
      body.addEventListener("transitionend", onCloseEnd);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Rendering                                                              */
  /* ---------------------------------------------------------------------- */

  OnRoadPrice.prototype.render = function () {
    if (!this.rows.length) return;
    this.renderSteps();
    this.renderPanel();
    this.renderActionbar();
    this.syncUrl();
  };

  OnRoadPrice.prototype.renderSteps = function () {
    var self = this;
    if (this.els.steps && !this.els.steps.classList.contains("onrp__steps")) {
      this.els.steps.classList.add("onrp__steps");
    }
    var html = STEPS.map(function (step, index) {
      var isDone = self.finished
        ? true
        : Boolean(self.selection[step]) && index !== self.stepIndex;
      var isCurrent = !self.finished && index === self.stepIndex;
      var reachable = isDone || isCurrent;
      var marker = isDone ? ICONS.check : String(index + 1);
      var classes = ["onrp__step"];
      if (isDone) classes.push("onrp__step--done");
      if (isCurrent) classes.push("onrp__step--current");

      return (
        '<li class="' +
        classes.join(" ") +
        '">' +
        '<button type="button" class="onrp__step-btn"' +
        ' data-onrp-action="step" data-index="' +
        index +
        '"' +
        (reachable ? "" : " disabled") +
        (isCurrent ? ' aria-current="step"' : "") +
        ">" +
        '<span class="onrp__step-marker">' +
        marker +
        "</span>" +
        '<span class="onrp__step-label">' +
        STEP_LABELS[step] +
        "</span>" +
        "</button>" +
        "</li>"
      );
    }).join("");

    this.els.steps.innerHTML = html;
  };

  OnRoadPrice.prototype.renderPanel = function () {
    if (this.finished) {
      this.renderResult();
      return;
    }
    var step = this.currentStep();
    if (step === "brand") return this.renderBrands();
    if (step === "model") return this.renderModels();
    if (step === "variant") return this.renderVariants();
    return this.renderStatePicker();
  };

  OnRoadPrice.prototype.stepHeader = function (title, description) {
    return (
      '<p class="onrp__eyebrow">Step ' +
      (this.stepIndex + 1) +
      " of " +
      STEPS.length +
      "</p>" +
      '<h2 class="onrp__step-title">' +
      escapeHtml(title) +
      "</h2>" +
      '<p class="onrp__step-desc">' +
      escapeHtml(description) +
      "</p>"
    );
  };

  OnRoadPrice.prototype.renderBrands = function () {
    var self = this;
    var cards = this.brands
      .map(function (brand) {
        var selected =
          self.selection.brand && self.selection.brand.id === brand.id;
        return (
          "<li>" +
          '<button type="button" class="onrp__card onrp__card--brand" aria-pressed="' +
          (selected ? "true" : "false") +
          '"' +
          ' data-onrp-action="select" data-step="brand" data-value="' +
          escapeHtml(brand.id) +
          '">' +
          '<span class="onrp__card-name">' +
          escapeHtml(brand.name) +
          "</span>" +
          (brand.tagline
            ? '<span class="onrp__card-sub">' +
              escapeHtml(brand.tagline) +
              "</span>"
            : "") +
          '<span class="onrp__card-select">Select' +
          ICONS.arrow +
          "</span>" +
          "</button>" +
          "</li>"
        );
      })
      .join("");

    this.els.panel.innerHTML =
      this.stepHeader(
        "Choose your brand",
        "Pick from our family of motorcycles.",
      ) +
      '<ul class="onrp__grid onrp__grid--brands">' +
      cards +
      "</ul>";
  };

  OnRoadPrice.prototype.renderModels = function () {
    var self = this;
    var brand = this.selection.brand;
    var models = this.models();

    if (!models.length) {
      this.els.panel.innerHTML =
        this.stepHeader(
          "Select model",
          "Available " + brand.name + " motorcycles.",
        ) +
        this.stateMarkup(
          "No models priced yet",
          "The sheet has no rows matching any " +
            brand.name +
            " product in this store. Pick another brand.",
        );
      return;
    }

    var showStartingPrice = this.config.showStartingPrice !== false;

    var cards = models
      .map(function (product) {
        var selected =
          self.selection.model && self.selection.model.id === product.id;
        var from = self.modelFromPrice(product);
        var price =
          from != null
            ? "Starts from " + formatMoney(from, self.config.currency)
            : product.priceMinFormatted;
        var priceSubHtml =
          showStartingPrice && price
            ? '<span class="onrp__card-sub onrp__card-sub--price">' +
              escapeHtml(price) +
              "</span>"
            : "";
        return (
          "<li>" +
          '<button type="button" class="onrp__card onrp__card--model" aria-pressed="' +
          (selected ? "true" : "false") +
          '"' +
          ' data-onrp-action="select" data-step="model" data-value="' +
          product.id +
          '">' +
          '<span class="onrp__card-name">' +
          escapeHtml(self.modelLabel(product)) +
          "</span>" +
          priceSubHtml +
          '<span class="onrp__card-arrow">' +
          ICONS.arrow +
          "</span>" +
          "</button>" +
          "</li>"
        );
      })
      .join("");

    this.els.panel.innerHTML =
      this.stepHeader(
        "Select model",
        "Available " + brand.name + " motorcycles.",
      ) +
      '<ul class="onrp__grid onrp__grid--models">' +
      cards +
      "</ul>";
  };

  OnRoadPrice.prototype.renderVariants = function () {
    var self = this;
    var product = this.selection.model;
    var variants = this.variants();

    if (!variants.length) {
      this.els.panel.innerHTML =
        this.stepHeader(
          "Choose variant",
          this.modelLabel(product) + " variants.",
        ) +
        this.stateMarkup(
          "No variants priced yet",
          "None of the " +
            this.modelLabel(product) +
            " variants in this store appear in the pricing sheet. Go back and pick another model.",
        );
      return;
    }

    if (
      this.config.autoSelectSingleVariant &&
      variants.length === 1 &&
      !this.selection.variant
    ) {
      this.selection.variant = variants[0];
    }

    var showExShowroomPrice = this.config.showExShowroomPrice !== false;

    var cards = variants
      .map(function (item) {
        var selected =
          self.selection.variant &&
          String(self.selection.variant.id) === String(item.id);
        var priceSubHtml = showExShowroomPrice
          ? '<span class="onrp__card-sub onrp__card-sub--price">Ex-Showroom ' +
            formatMoney(item.exShowroom, self.config.currency) +
            "</span>"
          : "";
        return (
          "<li>" +
          '<button type="button" class="onrp__card onrp__card--variant' +
          (item.available ? "" : " onrp__card--sold-out") +
          '"' +
          ' aria-pressed="' +
          (selected ? "true" : "false") +
          '"' +
          ' data-onrp-action="select" data-step="variant" data-value="' +
          item.id +
          '">' +
          '<span class="onrp__card-name">' +
          escapeHtml(item.label) +
          "</span>" +
          priceSubHtml +
          (item.available ? "" : '<span class="onrp__badge">Sold out</span>') +
          '<span class="onrp__card-arrow">' +
          ICONS.arrow +
          "</span>" +
          "</button>" +
          "</li>"
        );
      })
      .join("");

    this.els.panel.innerHTML =
      this.stepHeader(
        "Choose variant",
        this.modelLabel(product) + " variants.",
      ) +
      '<ul class="onrp__grid onrp__grid--variants">' +
      cards +
      "</ul>";
  };

  OnRoadPrice.prototype.renderStatePicker = function () {
    this.els.panel.innerHTML =
      this.stepHeader(
        "Select your state",
        "Search and pick from the list below.",
      ) +
      '<div class="onrp__picker">' +
      '<div class="onrp__search">' +
      ICONS.search +
      '<label class="onrp__visually-hidden" for="onrp-search-state">Search states</label>' +
      '<input id="onrp-search-state" type="search" autocomplete="off" data-onrp-search' +
      ' placeholder="Search states…" value="' +
      escapeHtml(this.query) +
      '">' +
      "</div>" +
      '<ul class="onrp__options" role="listbox" aria-label="States" data-onrp-options></ul>' +
      "</div>";
    this.renderOptions();
  };

  OnRoadPrice.prototype.renderOptions = function () {
    var list = this.root.querySelector("[data-onrp-options]");
    if (!list) return;

    var values = this.states();
    var query = norm(this.query);
    var filtered = query
      ? values.filter(function (value) {
          return norm(value).indexOf(query) !== -1;
        })
      : values;

    if (!filtered.length) {
      list.innerHTML =
        '<li><p class="onrp__state" style="border:0;background:none;padding:28px 24px">No state matches “' +
        escapeHtml(this.query) +
        "”. Try a shorter search.</p></li>";
      return;
    }

    var selected = this.selection.state;
    list.innerHTML = filtered
      .map(function (value) {
        var isSelected = selected && norm(selected) === norm(value);
        return (
          "<li>" +
          '<button type="button" class="onrp__option" role="option" aria-selected="' +
          (isSelected ? "true" : "false") +
          '"' +
          ' data-onrp-action="select" data-step="state" data-value="' +
          escapeHtml(value) +
          '">' +
          "<span>" +
          escapeHtml(value) +
          "</span>" +
          '<span class="onrp__option-check">' +
          ICONS.check +
          "</span>" +
          "</button>" +
          "</li>"
        );
      })
      .join("");
  };

  OnRoadPrice.prototype.renderActionbar = function () {
    var bar = this.els.actionbar;
    if (!bar) return;

    if (this.finished) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;

    this.els.backBtn.hidden = this.stepIndex === 0;

    var isLast = this.stepIndex === STEPS.length - 1;
    var label = isLast
      ? this.config.strings.checkPrice
      : this.config.strings.continue;
    this.els.continue.innerHTML =
      "<span>" + escapeHtml(label) + "</span>" + ICONS.arrow;
    this.els.continue.disabled = !this.canContinue();
  };

  /* --- Result ----------------------------------------------------------- */

  OnRoadPrice.prototype.renderResult = function () {
    var row = this.priceRow();
    var self = this;

    if (!row) {
      this.els.panel.innerHTML = this.stateMarkup(
        "No price for this combination",
        "The sheet has no row for " +
          this.selection.state +
          " with this variant. Pick a different state or contact your nearest dealership.",
      );
      return;
    }

    var sum = BREAKDOWN_ROWS.reduce(function (total, item) {
      return total + (row[item.key] || 0);
    }, 0);
    var total = row.onRoad != null ? row.onRoad : sum;

    if (row.onRoad != null && sum > 0 && Math.abs(sum - row.onRoad) > 1) {
      console.warn(
        "[on-road-price] Sheet total (" +
          row.onRoad +
          ") does not equal the sum of its components (" +
          sum +
          ") for " +
          row.model +
          " / " +
          row.variant +
          " / " +
          row.state +
          ". Showing the sheet total.",
      );
    }

    var summary = [
      ["Brand", this.selection.brand.name],
      ["Model", this.modelLabel(this.selection.model)],
      ["Variant", this.selection.variant.label],
      ["State", row.state],
    ]
      .map(function (pair) {
        return (
          "<div><dt>" +
          escapeHtml(pair[0]) +
          "</dt><dd>" +
          escapeHtml(pair[1]) +
          "</dd></div>"
        );
      })
      .join("");

    var lines = BREAKDOWN_ROWS.filter(function (item) {
      return row[item.key] != null;
    })
      .map(function (item, index) {
        return (
          '<div class="onrp__line" style="--onrp-line-index: ' +
          index +
          '">' +
          "<dt>" +
          item.label +
          "</dt>" +
          "<dd>" +
          formatMoney(row[item.key], self.config.currency) +
          "</dd>" +
          "</div>"
        );
      })
      .join("");

    var variant = this.selection.variant;
    var canBuy = Boolean(variant && variant.available);
    var testRideUrl = this.buildTestRideUrl();

    this.els.panel.innerHTML =
      '<div class="onrp__result">' +
      '<section class="onrp__summary" aria-label="Your selection">' +
      '<p class="onrp__summary-label">Your selection</p>' +
      '<dl class="onrp__summary-grid">' +
      summary +
      "</dl>" +
      "</section>" +
      '<section class="onrp__price" aria-label="On-road price">' +
      '<p class="onrp__price-label">' +
      ICONS.pin +
      "On-Road Price · " +
      escapeHtml(row.state) +
      "</p>" +
      '<p class="onrp__price-value">' +
      formatMoney(total, this.config.currency) +
      "</p>" +
      '<p class="onrp__price-note">Total on-road price — all inclusive</p>' +
      "</section>" +
      '<section class="onrp__breakdown">' +
      '<button type="button" class="onrp__breakdown-toggle" data-onrp-action="toggle-breakdown"' +
      ' aria-expanded="' +
      (this.breakdownOpen ? "true" : "false") +
      '" aria-controls="onrp-breakdown">' +
      "<span>Price breakdown</span>" +
      ICONS.chevron +
      "</button>" +
      '<div class="onrp__breakdown-body" id="onrp-breakdown" data-onrp-breakdown-body' +
      (this.breakdownOpen ? "" : " hidden") +
      ">" +
      '<dl class="onrp__lines">' +
      lines +
      '<div class="onrp__line onrp__line--total">' +
      "<dt>Total on-road price</dt>" +
      "<dd>" +
      formatMoney(total, this.config.currency) +
      "</dd>" +
      "</div>" +
      "</dl>" +
      "</div>" +
      "</section>" +
      '<div class="onrp__ctas">' +
      (testRideUrl
        ? '<a class="onrp__btn  onrp__btn--primary" href="' +
          escapeHtml(testRideUrl) +
          '">' +
          escapeHtml(this.config.strings.testRide) +
          "</a>"
        : "") +

      "</div>" +
      '<p class="onrp__error-inline" data-onrp-cart-error role="alert" hidden></p>' +
      (this.config.strings.disclaimer
        ? '<div class="onrp__note">' +
          ICONS.info +
          "<p>" +
          escapeHtml(this.config.strings.disclaimer) +
          "</p></div>"
        : "") +
      "</div>";

    this.announce(
      "On-road price for " +
        row.state +
        " is " +
        formatMoney(total, this.config.currency) +
        ".",
    );
  };

  OnRoadPrice.prototype.buildTestRideUrl = function () {
    var base = this.config.testRideUrl;
    if (!base) return "";
    var params = new URLSearchParams();
    params.set("brand", this.selection.brand.name);
    params.set("model", this.selection.model.handle);
    params.set("variant", this.selection.variant.label);
    params.set("state", this.selection.state);
    return base + (base.indexOf("?") === -1 ? "?" : "&") + params.toString();
  };

  /* --- Non-happy paths -------------------------------------------------- */

  OnRoadPrice.prototype.stateMarkup = function (title, body, retry) {
    return (
      '<div class="onrp__state">' +
      "<h3>" +
      escapeHtml(title) +
      "</h3>" +
      "<p>" +
      escapeHtml(body) +
      "</p>" +
      (retry
        ? '<button type="button" class="onrp__btn onrp__btn--ghost" data-onrp-action="retry">Try again</button>'
        : "") +
      "</div>"
    );
  };

  OnRoadPrice.prototype.renderState = function (title, body, retry) {
    if (this.els.steps) this.els.steps.innerHTML = "";
    this.els.panel.innerHTML = this.stateMarkup(title, body, retry);
    if (this.els.actionbar) this.els.actionbar.hidden = true;
  };

  OnRoadPrice.prototype.renderSkeleton = function () {
    this.els.panel.innerHTML =
      '<div class="onrp__skeleton"><span></span><span></span><span></span></div>';
    if (this.els.actionbar) this.els.actionbar.hidden = true;
  };

  /* ---------------------------------------------------------------------- */
  /* Boot                                                                   */
  /* ---------------------------------------------------------------------- */

  function init(scope) {
    (scope || document)
      .querySelectorAll("[data-onrp-root]")
      .forEach(function (root) {
        if (root.dataset.onrpReady === "true") return;
        root.dataset.onrpReady = "true";
        new OnRoadPrice(root);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: rebuild when the section is re-rendered.
  document.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });

  // Clear any stuck loading state when the page is restored from the
  // back-forward cache (e.g. shopper hits Back after checkout).
  window.addEventListener("pageshow", function (event) {
    if (!event.persisted) return;
    document.querySelectorAll(".onrp__btn.is-loading").forEach(function (btn) {
      btn.classList.remove("is-loading");
      btn.removeAttribute("aria-busy");
    });
  });
})();
